import {
  Table,
  Col,
  Row,
  Space,
  Checkbox,
  Tag,
  Typography,
  message,
  Popconfirm,
  Button,
  Divider,
  Input,
} from "antd"
import React, { useEffect, useState, useMemo } from "react"
import PageHeader from "../../../components/Elements/PageHeader"
import {
  UnorderedListOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  QuestionCircleOutlined,
  ClearOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import { db, storage } from "../../../firebase/config"
import { collection, deleteDoc, doc, onSnapshot, orderBy, query } from "firebase/firestore"
import { FaTrash } from "react-icons/fa6"
import { useCollection } from "../../../hooks/useCollection"
import UpdatePlate from "./UpdatePlate"
import NewVivaPlateModal from "./NewVivaPlateModal"
import { deleteObject, listAll, ref } from "firebase/storage"

const { Text } = Typography

export default function ArsomaPlates() {
  const { data } = useCollection("clients")

  const [arsomaPlates, setArsomaPlate] = useState([])
  const [arsomaPlatesLoading, setArsomaPlatesLoading] = useState(false)
  const [filteredInfo, setFilteredInfo] = useState({})
  const [showEllipsis, setShowEllipsis] = useState(true)
  const [lastPlate, setLastPlate] = useState({})
  const [codeSearch, setCodeSearch] = useState("")

  // row selection
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    setArsomaPlatesLoading(true)
    const vivaPlateRef = collection(db, "vivaPlateList")
    const plateQuery = query(vivaPlateRef, orderBy("codeNumber", "desc"))

    const unsubscribe = onSnapshot(plateQuery, (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        results.push({ ...doc.data(), id: doc.id })
      })
      setArsomaPlate(results)
      setLastPlate(results[0])
      setArsomaPlatesLoading(false)
    })
    return () => unsubscribe()
  }, [])

  // ─── code search filter ──────────────────────────────────────────────────────
  const displayData = useMemo(() => {
    if (!codeSearch.trim()) return arsomaPlates
    const needle = codeSearch.trim().toLowerCase()
    return arsomaPlates.filter((r) => r.code?.toLowerCase().includes(needle))
  }, [arsomaPlates, codeSearch])

  // ─── dynamic filters ─────────────────────────────────────────────────────────
  const clientFilter = useMemo(
    () =>
      [...new Set(arsomaPlates.map((i) => i.client).filter(Boolean))]
        .sort()
        .map((v) => ({ text: v, value: v })),
    [arsomaPlates],
  )

  const productFilter = useMemo(
    () =>
      [...new Set(arsomaPlates.map((i) => i.product).filter(Boolean))]
        .sort()
        .map((v) => ({ text: v, value: v })),
    [arsomaPlates],
  )

  const layoutFilter = useMemo(
    () =>
      [...new Set(arsomaPlates.map((i) => i.layoutDesign).filter(Boolean))]
        .sort()
        .map((v) => ({ text: v, value: v })),
    [arsomaPlates],
  )

  const deletePlate = async (data) => {
    const plateRef = doc(db, "vivaPlateList", data.id)
    try {
      const folderRef = ref(storage, `vivaPlates/${data.code}`)
      const { items } = await listAll(folderRef)
      await Promise.all(items.map((itemRef) => deleteObject(itemRef)))
      await deleteDoc(plateRef)
    } catch (error) {
      message.error("error happened, check console!")
      console.log(error.message)
    } finally {
      message.success("Deleted!")
    }
  }

  const deleteSelected = async () => {
    setDeleting(true)
    const selected = arsomaPlates.filter((r) => selectedRowKeys.includes(r.id))
    let successCount = 0
    let errorCount = 0

    await Promise.all(
      selected.map(async (plate) => {
        try {
          const folderRef = ref(storage, `vivaPlates/${plate.code}`)
          const { items } = await listAll(folderRef)
          await Promise.all(items.map((itemRef) => deleteObject(itemRef)))
          await deleteDoc(doc(db, "vivaPlateList", plate.id))
          successCount++
        } catch (err) {
          console.error("Failed to delete plate:", plate.code, err)
          errorCount++
        }
      }),
    )

    setSelectedRowKeys([])
    setDeleting(false)

    if (errorCount === 0) {
      message.success(`Deleted ${successCount} plate${successCount > 1 ? "s" : ""}`)
    } else {
      message.warning(`Deleted ${successCount}, failed ${errorCount}`)
    }
  }

  const onFilterChange = (pagination, filters, sorter, extra) => {
    setFilteredInfo(filters)
  }

  const clearAllFilters = () => {
    setFilteredInfo({})
    setCodeSearch("")
    setSelectedRowKeys([])
  }

  // ─── row selection ────────────────────────────────────────────────────────────
  const allKeys = displayData.map((r) => r.id)
  const allSelected = allKeys.length > 0 && selectedRowKeys.length === allKeys.length
  const someSelected = selectedRowKeys.length > 0 && !allSelected

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedRowKeys([])
    } else {
      setSelectedRowKeys(allKeys)
    }
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  }

  const defaultColumns = [
    {
      title: "Code",
      dataIndex: "code",
      align: "left",
      width: "7%",
    },
    {
      title: "Client",
      dataIndex: "client",
      align: "left",
      ellipsis: showEllipsis,
      filters: clientFilter,
      onFilter: (value, record) => record.client === value,
      filterSearch: true,
      filteredValue: filteredInfo.client || null,
    },
    {
      title: "Product",
      dataIndex: "product",
      align: "left",
      ellipsis: showEllipsis,
      filters: productFilter,
      onFilter: (value, record) => record.product === value,
      filterSearch: true,
      filterMultiple: true,
      filteredValue: filteredInfo.product || null,
    },
    {
      title: "Important",
      dataIndex: "important",
      align: "center",
      ellipsis: showEllipsis,
      width: "5%",
      render: (value) =>
        value === "none" ? "none" : <Text style={{ color: "red" }}>{value}</Text>,
    },
    {
      title: "Knife Nr.",
      dataIndex: "knifeNumber",
      align: "center",
      width: "5%",
    },
    {
      title: "Rows",
      dataIndex: "rows",
      align: "center",
      width: "5%",
    },
    {
      title: "Loc.",
      dataIndex: "plateLocation",
      align: "center",
      width: "5%",
      ellipsis: showEllipsis,
    },
    {
      title: "Layout Design",
      dataIndex: "layoutDesign",
      align: "left",
      ellipsis: showEllipsis,
      filters: layoutFilter,
      onFilter: (value, record) => record.layoutDesign === value,
      filterSearch: true,
      filterMultiple: true,
      filteredValue: filteredInfo.layoutDesign || null,
    },
    {
      title: "W",
      dataIndex: "width",
      align: "center",
      width: "5%",
      sorter: { compare: (a, b) => a.width - b.width, multiple: 2 },
    },
    {
      title: "H",
      dataIndex: "height",
      align: "center",
      width: "5%",
      sorter: { compare: (a, b) => a.height - b.height, multiple: 2 },
    },
    {
      title: "Action",
      align: "center",
      width: "5%",
      render: (_, record) => (
        <Space>
          <Popconfirm
            title="Delete the plate"
            description="Are you sure to delete this plate"
            onConfirm={() => deletePlate(record)}
            icon={<QuestionCircleOutlined style={{ color: "red" }} />}>
            <FaTrash style={{ color: "red", cursor: "pointer" }} />
          </Popconfirm>
          <UpdatePlate clientList={data} data={record} />
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[10, 10]}>
        <Col span={24}>
          <PageHeader text="Viva plates" icon={<UnorderedListOutlined />} />
        </Col>
        <Col span={24}>
          <NewVivaPlateModal lastPlate={lastPlate} />
        </Col>

        {/* Toolbar */}
        <Col span={24}>
          <Space wrap split={<Divider type="vertical" />}>
            {/* Code search */}
            <Input
              placeholder="Search code…"
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={codeSearch}
              onChange={(e) => setCodeSearch(e.target.value)}
              allowClear
              size="small"
              style={{ width: 160 }}
            />

            {/* Select / deselect all */}
            <Checkbox checked={allSelected} indeterminate={someSelected} onChange={toggleSelectAll}>
              {allSelected ? "Deselect all" : `Select all (${displayData.length})`}
            </Checkbox>

            {selectedRowKeys.length > 0 && (
              <>
                <Tag color="blue">{selectedRowKeys.length} selected</Tag>
                <Popconfirm
                  title={`Delete ${selectedRowKeys.length} plate${selectedRowKeys.length > 1 ? "s" : ""}?`}
                  description="This cannot be undone. All files will be permanently deleted."
                  onConfirm={deleteSelected}
                  okText="Delete"
                  okButtonProps={{ danger: true, loading: deleting }}
                  icon={<QuestionCircleOutlined style={{ color: "red" }} />}>
                  <Button size="small" danger loading={deleting}>
                    Delete selected ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
              </>
            )}

            {/* Shorten columns */}
            <Checkbox checked={showEllipsis} onChange={() => setShowEllipsis((v) => !v)}>
              Shorten columns
            </Checkbox>

            {/* Clear filters */}
            <Button size="small" type="text" onClick={clearAllFilters}>
              <ClearOutlined /> Clear filters
            </Button>
          </Space>
        </Col>

        <Col span={24}>
          <Table
            size="small"
            bordered
            rowKey={(record) => record.id}
            loading={arsomaPlatesLoading}
            columns={defaultColumns}
            onChange={onFilterChange}
            dataSource={displayData}
            rowSelection={rowSelection}
            pagination={{ pageSize: 3000, showSizeChanger: false, hideOnSinglePage: true }}
            expandable={{
              expandedRowRender: (record) => (
                <Row gutter={[15, 15]}>
                  <Col span={24}>
                    Width & Height : <Tag color="blue">{record.widthXHeight}</Tag>
                  </Col>
                  <Col span={24}>
                    Client : <Tag color="blue">{record.client}</Tag>
                  </Col>
                  <Col span={24}>
                    Product : <Tag color="blue">{record.product}</Tag>
                  </Col>
                  <Col span={24}>
                    Layout Design : <Tag color="blue">{record.layoutDesign}</Tag>
                  </Col>
                  <Col span={24}>
                    Important : <Tag color="blue">{record.important}</Tag>
                  </Col>
                  <Col span={24}>
                    Just code : <Tag color="blue">{record.codeNumber}</Tag>
                  </Col>
                </Row>
              ),
              expandIcon: ({ expanded, onExpand, record }) =>
                expanded ? (
                  <MinusCircleOutlined onClick={(e) => onExpand(record, e)} />
                ) : (
                  <PlusCircleOutlined onClick={(e) => onExpand(record, e)} />
                ),
            }}
          />
        </Col>
      </Row>
    </div>
  )
}
